"use client";

import ActivitySelect, {
  activityTypeTextFilter,
} from "@/components/ActivitySelect";
import type { DiscoverSelection } from "@/components/discover/filters/selection";
import { useTabs } from "@/components/tabs/SwipeableTabsController";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDragRegion,
  BottomSheetTitle,
  BottomSheetTrigger,
} from "@/components/ui/bottom-sheet";
import {
  CommandBareInput,
  CommandInput,
  CommandProvider,
  Command as CommandUI,
} from "@/components/ui/command";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { SelectorTrigger } from "@/components/ui/selector-trigger";
import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { useActivityTypesQuery } from "@/hooks/queries";
import { useIsMobile } from "@/hooks/breakpoints";
import { usePendingChangesDetection } from "@/hooks/usePendingChangesDetection";
import { usePendingSearch } from "@/hooks/usePendingSearch";
import { trackFirstDiscoverInteraction } from "@/lib/analytics/discoverInteractions";
import { useLocale, useTranslations } from "next-intl";
import pendingSearch from "@/lib/pendingSearch";
import { areArraysEqual, cn } from "@/lib/utils";
import type { Coords } from "@/types/places";
import { createTaxonomyFormatter } from "@/utils/formatTaxonomy";
import type { LocaleType } from "@/constants/locales";
import {
  getLocaleDefaultArea,
  type MapAreaId,
  mapAreaIds,
} from "@/utils/location";
import { ChevronDownIcon } from "lucide-react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useLayoutEffect, useRef, useState } from "react";

export default function ActivitiesDropdown({
  coords,
  selection,
  onActivitySelected,
}: {
  coords: Coords | null;
  selection: DiscoverSelection;
  /** Whether this dropdown is in expanded visual state (controls flex behavior) */
  /** Callback when an activity is selected (for auto-open flow) */
  onActivitySelected?: () => void;
}) {
  const t = useTranslations();
  const locale = useLocale() as LocaleType;
  const { selectedTab } = useTabs();
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();

  // Get current area from URL
  const areaParam = searchParams.get("area");
  const currentArea = mapAreaIds.includes(areaParam as MapAreaId)
    ? (areaParam as MapAreaId)
    : getLocaleDefaultArea(locale);

  // The other mounted search bar can write the draft. Subscribing keeps this
  // label in step with that write.
  const pendingActivities = usePendingSearch().activities;

  const selectedActivities = pendingActivities ?? selection.activities;

  const format = createTaxonomyFormatter(t);
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const {
    activeTrigger,
    setTriggerOpen,
    setHasPendingChanges,
    initialTrigger,
    setInitialTrigger,
    resetAllTriggers,
    isAnyTriggerOpen,
  } = useSearchTriggerContext();

  const { hasPendingChanges } = usePendingChangesDetection({
    currentArea,
    currentActivities: selectedActivities,
  });

  // Sync local state with context state to ensure exclusivity
  // Open when context says we should be open, close when another dropdown opens
  useLayoutEffect(() => {
    const contextIsOpen = activeTrigger === "activities-dropdown";

    if (contextIsOpen !== isOpen) {
      setIsOpen(contextIsOpen);
    }
  }, [activeTrigger, isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // setTriggerOpen now handles exclusive behavior automatically
      setTriggerOpen("activities-dropdown", true);
      setIsOpen(true);
      // Set initialTrigger when opening (only if no trigger is already set)
      if (initialTrigger === null) {
        setInitialTrigger("activity");
      }
    } else {
      // Only update context if we're actually open in context
      if (activeTrigger === "activities-dropdown") {
        setTriggerOpen("activities-dropdown", false);
      }
      setIsOpen(false);
      // When closing, check if there are pending changes
      setHasPendingChanges(hasPendingChanges());
    }
  };

  const setSelectedActivities = (activities: string[]) => {
    trackFirstDiscoverInteraction("activity_selector");
    pendingSearch.setActivities(activities);

    const hasChanges = !areArraysEqual(activities, selectedActivities);
    setHasPendingChanges(hasChanges);

    // Notify parent before closing (for auto-open flow)
    onActivitySelected?.();

    handleOpenChange(false);
  };

  const activityTypes = useActivityTypesQuery({
    coords,
    type: selectedTab,
    selection,
  });

  // Activity icon for the selector (used in both mobile and desktop)
  const activityIcon = (
    <div className="shrink-0 size-8 rounded-sm overflow-clip">
      <Image
        src="/search/search-activity.png"
        alt=""
        width={32}
        height={32}
        className="rounded-sm"
        sizes="32px"
      />
    </div>
  );

  const triggerButton = (
    <SelectorTrigger
      className={cn("shrink-0 border-0 rounded-l-full h-10")}
      leftAdornment={activityIcon}
      rightAdornment={<ChevronDownIcon className="h-5 w-5" />}
    >
      {selectedActivities.length > 0
        ? selectedActivities.map((activity) => (
            <span key={activity}>{format.activity(activity)}</span>
          ))
        : t("activitiesDropdown.selectActivities")}
    </SelectorTrigger>
  );

  const activitiesContent = (
    <ActivitySelect
      items={activityTypes.map(({ activityType, count }) => ({
        value: activityType,
        count,
      }))}
      value={selectedActivities}
      onChange={(next) => setSelectedActivities(next)}
      listOnly
      allLabel={t("activitiesDropdown.all")}
    />
  );

  const activityLabel =
    selectedActivities.length > 0
      ? selectedActivities.map((a) => format.activity(a)).join(", ")
      : t("activitiesDropdown.selectActivities");

  const heightClass = isAnyTriggerOpen ? "h-12" : "h-10";

  if (!isMobile) {
    return (
      <CommandProvider
        className="w-full min-w-0"
        filter={activityTypeTextFilter}
      >
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverAnchor asChild>
            <div
              ref={anchorRef}
              className="w-full"
              data-search-bar-component="activity-anchor"
            >
              {isOpen ? (
                <div
                  className={cn(
                    "flex w-full items-center gap-2 px-4 bg-white rounded-full",
                    "animate-in fade-in-0 zoom-in-95 duration-200",
                    heightClass,
                  )}
                >
                  {activityIcon}
                  <CommandBareInput
                    placeholder={t("activitiesDropdown.searchPlaceholder")}
                    autoFocus
                    className="flex-1"
                  />
                </div>
              ) : (
                // Closed state: Entire area is clickable
                <button
                  type="button"
                  onClick={() => handleOpenChange(true)}
                  className={cn(
                    "flex w-full items-center gap-2 px-4 bg-transparent cursor-pointer",
                    heightClass,
                  )}
                >
                  {activityIcon}
                  <span
                    className={cn(
                      "flex-1 min-w-0 text-left text-base font-medium leading-6 truncate",
                      selectedActivities.length > 0
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    {activityLabel}
                  </span>
                </button>
              )}
            </div>
          </PopoverAnchor>
          <PopoverContent
            sideOffset={15}
            className="w-[320px]"
            align="start"
            onInteractOutside={(e) => {
              const target = e.target as HTMLElement | null;
              // Only prevent closing when clicking on our own anchor
              if (
                anchorRef.current &&
                target &&
                anchorRef.current.contains(target)
              ) {
                e.preventDefault();
                return;
              }
              // Prevent closing when clicking on location selector anchor
              // This allows the location button's onClick to fire and open the location selector
              if (target) {
                const locationAnchor = target.closest(
                  '[data-search-bar-component="location-anchor"]',
                );
                if (locationAnchor) {
                  e.preventDefault();
                  return;
                }
              }
              // Let other search bar components trigger their own exclusive open
            }}
            onEscapeKeyDown={() => resetAllTriggers()}
          >
            <div className="w-full max-h-[640px] flex flex-col py-2 overflow-x-hidden no-scrollbar">
              {activitiesContent}
            </div>
          </PopoverContent>
        </Popover>
      </CommandProvider>
    );
  }

  return (
    <BottomSheet open={isOpen} onOpenChange={handleOpenChange}>
      <BottomSheetTrigger asChild className="w-full">
        {triggerButton}
      </BottomSheetTrigger>

      <BottomSheetContent
        className="max-h-[90vh]"
        onOpenChange={handleOpenChange}
        open={isOpen}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <BottomSheetDragRegion className="px-4 pb-4">
          <BottomSheetTitle className="text-center text-lg font-semibold">
            {t("activitiesDropdown.title")}
          </BottomSheetTitle>
        </BottomSheetDragRegion>

        <div className="flex-1 overflow-auto no-scrollbar mt-4 pb-6 px-4 min-h-[75vh]">
          <CommandUI
            className="flex flex-col h-full min-h-0"
            filter={activityTypeTextFilter}
          >
            <CommandInput
              placeholder={t("activitiesDropdown.searchPlaceholder")}
            />
            <div className="mt-4">{activitiesContent}</div>
          </CommandUI>
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
}
